import { Component } from '@theme/component';

/**
 * Customer social-proof loop.
 *
 * Reads its `_customer` child blocks (each exposes `ref="people[]"` with
 * `data-name` / `data-verified` and an `<img>`), then renders a rotating
 * "window" of N customers as an overlapping avatar stack plus a name
 * sentence, cycling through the full set with a soft cross-fade.
 *
 * Progressive enhancement: the server-rendered source list stays in the DOM
 * (visually hidden once active) so the content works without JS and for
 * assistive tech / crawlers.
 *
 * @typedef {object} Person
 * @property {string} name
 * @property {boolean} verified
 * @property {HTMLImageElement|null} image
 *
 * @typedef {object} Refs
 * @property {HTMLElement} viewport
 * @property {HTMLElement} avatars
 * @property {HTMLElement} sentence
 * @property {HTMLElement[]} [people]
 * @property {HTMLTemplateElement} [badge]
 *
 * @extends {Component<Refs>}
 */
export class CustomerLoop extends Component {
  requiredRefs = ['viewport', 'avatars', 'sentence'];

  #index = 0;

  /** @type {number|undefined} */
  #timer = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.#suspend);
    this.addEventListener('mouseleave', this.#resume);
    document.addEventListener('visibilitychange', this.#onVisibility);

    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#suspend();
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  // Re-paint and restart after a Section Rendering API update (theme editor).
  updatedCallback() {
    super.updatedCallback();
    this.#index = 0;
    this.#start();
  }

  #start() {
    this.#suspend();

    const people = this.#people;
    if (people.length === 0) return;

    this.classList.add('customer-loop--ready');
    this.#paint(this.#window());

    if (people.length > this.#visible && this.#interval > 0) {
      this.#timer = setInterval(() => {
        if (this.matches(':hover') || document.hidden) return;
        this.#advance();
      }, this.#interval);
    }
  }

  #advance() {
    const people = this.#people;
    if (people.length <= this.#visible) return;

    this.#index = (this.#index + this.#visible) % people.length;
    const next = this.#window();

    if (this.#reduceMotion) {
      this.#paint(next);
      return;
    }

    const { viewport } = this.refs;
    viewport.classList.add('customer-loop__viewport--out');

    let swapped = false;
    let fallback = 0;

    const swap = () => {
      if (swapped) return;
      swapped = true;
      clearTimeout(fallback);
      viewport.removeEventListener('transitionend', swap);

      this.#paint(next);
      requestAnimationFrame(() => viewport.classList.remove('customer-loop__viewport--out'));
    };

    // Swap once the fade-out finishes, with a timeout fallback in case the
    // transition never fires (e.g. element off-screen / interrupted).
    viewport.addEventListener('transitionend', swap);
    fallback = setTimeout(swap, 400);
  }

  /** @returns {Person[]} */
  get #people() {
    const items = this.refs.people ?? [];

    return items
      .map((el) => ({
        name: el.dataset.name?.trim() ?? '',
        verified: el.dataset.verified === 'true',
        image: /** @type {HTMLImageElement|null} */ (el.querySelector('img')),
      }))
      .filter((person) => person.image || person.name);
  }

  get #visible() {
    const value = parseInt(this.dataset.visible ?? '3', 10);
    const count = this.#people.length || 1;
    return Math.max(1, Math.min(Number.isNaN(value) ? 3 : value, count));
  }

  get #interval() {
    const value = parseFloat(this.dataset.interval ?? '4');
    return Number.isNaN(value) ? 0 : value * 1000;
  }

  get #showBadges() {
    return this.dataset.badges !== 'false';
  }

  get #reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** The current window of people, wrapping around the end. @returns {Person[]} */
  #window() {
    const people = this.#people;
    const visible = this.#visible;
    const out = [];

    for (let i = 0; i < visible; i++) {
      out.push(people[(this.#index + i) % people.length]);
    }

    return out;
  }

  /** @param {Person[]} window */
  #paint(window) {
    this.#paintAvatars(window);
    this.#paintSentence(window);
  }

  /** @param {Person[]} window */
  #paintAvatars(window) {
    const { avatars } = this.refs;
    avatars.replaceChildren();

    window.forEach((person, i) => {
      if (!person.image) return;

      const item = document.createElement('li');
      item.className = 'customer-loop__avatar';
      item.style.setProperty('--i', String(i));

      const img = /** @type {HTMLImageElement} */ (person.image.cloneNode(true));
      img.removeAttribute('ref');
      item.appendChild(img);

      avatars.appendChild(item);
    });
  }

  /** @param {Person[]} window */
  #paintSentence(window) {
    const { sentence } = this.refs;
    sentence.replaceChildren();

    window.forEach((person, i) => {
      const name = document.createElement('span');
      name.className = 'customer-loop__name';
      name.textContent = person.name;
      sentence.appendChild(name);

      if (person.verified && this.#showBadges) {
        const badge = this.#badge();
        if (badge) sentence.appendChild(badge);
      }

      if (i < window.length - 1) {
        sentence.appendChild(document.createTextNode(', '));
      }
    });

    const connector = this.dataset.connector?.trim();
    if (connector) sentence.appendChild(document.createTextNode(` ${connector} `));

    const countText = this.dataset.count?.trim();
    if (countText) {
      const count = document.createElement('span');
      count.className = 'customer-loop__count';
      count.textContent = countText;
      sentence.appendChild(count);
      sentence.appendChild(document.createTextNode(' '));
    }

    const suffix = this.dataset.suffix?.trim();
    if (suffix) {
      const span = document.createElement('span');
      span.className = 'customer-loop__suffix';
      span.textContent = suffix;
      sentence.appendChild(span);
    }
  }

  /** @returns {Node|null} */
  #badge() {
    const template = this.refs.badge;
    if (!(template instanceof HTMLTemplateElement)) return null;
    return template.content.cloneNode(true);
  }

  #suspend = () => {
    clearInterval(this.#timer);
    this.#timer = undefined;
  };

  #resume = () => {
    if (this.#timer || document.hidden) return;
    this.#start();
  };

  #onVisibility = () => {
    if (document.hidden) this.#suspend();
    else this.#resume();
  };
}

if (!customElements.get('customer-loop')) {
  customElements.define('customer-loop', CustomerLoop);
}
